import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import Block from "../../components/Block";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  bulkPushOffAutoOrders,
  listAccountAutomaticOrders,
  listVariants,
  loadDisabledOrders,
} from "../../distflowAPI/ordersApi";
import accountsApi from "../../distflowAPI/accountsApi";
import { useSearchParamsState } from "react-use-search-params-state";
import AccountSelect from "../../components/AsyncSelects/AccountSelect";
import TagSelect from "../../components/AsyncSelects/TagSelect";
import { getTag } from "../../distflowAPI/tagsApi";
import debounce from "lodash.debounce";
import AutoOrderConfigurations from "./AutoOrderConfigurations";
import AutoOrdersProductsList from "./AutoOrdersProductsList";
import AutoOrdersAddProduct from "./AutoOrdersAddProduct";
import DatePickerCustom2 from "../../components/DatePicker2";
import { DATE_FORMAT, DATE_FORMAT_FRONT } from "../../utils/constants";
import dayjs from "dayjs";

const filtersDefaults = {
  accountId: { type: "number", default: null },
  category: { type: "string", default: "" },
  vendor: { type: "string", default: "" },
  search: { type: "string", default: "" },
  sku: { type: "string", default: "" },
  day: { type: "string", default: "all" },
  tags: { type: "string", default: "" },
};

const ManageAutoOrders = () => {
  const [filterParams, setFilterParams] = useSearchParamsState(filtersDefaults);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDisableMultiple, setOpenDisableMultiple] = useState(false);
  const [openResumeDisabled, setOpenResumeDisabled] = useState(false);
  const [accountAutomaticOrdersLoading, setAccountAutomaticOrdersLoading] =
    useState(false);
  const [variants, setVariants] = useState([]);
  const [accountOrders, setAccountOrders] = useState([]);
  const [account, setAccount] = useState({
    label: "",
    value: filterParams.accountId || null,
  });

  const [selectedTag, setSelectedTag] = useState([]);
  const [disabledAccounts, setDisabledAccounts] = useState([])

  const refreshDisabledAccounts = useCallback(() => {
    loadDisabledOrders().then((res) => {
      setDisabledAccounts(res.data);
    });
  }, []);
  useEffect(() => {
    refreshDisabledAccounts();
  }, [refreshDisabledAccounts]);
  useEffect(() => {
    if (filterParams.accountId) {
      accountsApi
        .findById(filterParams.accountId)
        .then((res) =>
          setAccount({ value: res.data.id, label: res.data.name }),
        );
    }
    if (filterParams.tags) {
      const tags = filterParams.tags.split(",");
      tags.forEach(async (tagId) => {
        await getTag(tagId).then((res) => {
          setSelectedTag((prev) => [
            ...prev,
            { value: res.data.id, label: res.data.name },
          ]);
        });
      });
    }
  }, []);

  function updateAccountRelated() {
    if (account && account.value) {
      listVariants(20, 1, [], searchParams).then((res) => {
        setVariants(res.results);
      });
      setAccountAutomaticOrdersLoading(true);
      listAccountAutomaticOrders(account.value, filterParams.day)
        .then((res) => setAccountOrders(res.data.results))
        .finally(() => {
          setAccountAutomaticOrdersLoading(false);
        });
    } else {
      setVariants([]);
    }
  }

  useEffect(() => {
    updateAccountRelated();
  }, [searchParams]);

  const handleBackClick = () => {
    navigate(`/orders?date=${searchParams.get("date")}`);
  };

  const removeOrders = () => {
    setAccountOrders([]);
  };

  return (
    <Box maxWidth="1600px" margin=" 30px auto 0">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems="center"
        justifyContent="space-between"
        spacing="20px"
        mb={5}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={{ xs: "space-between", sm: "flex-srart" }}
          sx={{ width: { xs: "100%", sm: "initial" }, gap: "20px" }}
        >
          <Button
            className="btn-height"
            onClick={handleBackClick}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Typography
            variant="pageTitle"
            noWrap
            component="p"
            data-testid="page-title"
          >
            Order Configurations
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          sx={{
            gap: "11px",
            "@media(max-width: 480px)": {
              width: "100%",
              button: { width: "100%" },
            },
          }}
        >
          <Button color="primary" variant="contained">
            Save
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenDisableMultiple(true)}
          >
            Disable Multiple accounts
          </Button>
          {disabledAccounts?.length>0 && <Button
            variant="contained"
            color="success"
            onClick={() => setOpenResumeDisabled(true)}
          >
            Resume Disables Multiple accounts
          </Button>}
          
        </Stack>
      </Stack>
      <DisableMultiple
        open={openDisableMultiple}
        setOpen={setOpenDisableMultiple}
        onSuccess={refreshDisabledAccounts}
        updateAccountRelated={updateAccountRelated}
      />
      <ResumeDisabledAccountsModal
        open={openResumeDisabled}
        setOpen={setOpenResumeDisabled}
        disabledAccounts={disabledAccounts}
        onSuccess={refreshDisabledAccounts}
        updateAccountRelated={updateAccountRelated}
      />
      <Grid container columnSpacing={{ xs: 1, sm: "20px" }} margin="36px 0 0">
        <Grid item width="100%" xl={6}>
          <Block>
            <Box data-testid="add-product-form">
              <AutoOrdersAddProduct
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                setAccount={setAccount}
                account={account}
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                isShowSearchAccount
                removeOrders={removeOrders}
              />
            </Box>
          </Block>

          <Box mt="-13px" data-testid="auto-order-products-list">
            <AutoOrdersProductsList
              account={account}
              updateAccountRelated={updateAccountRelated}
              variants={variants}
              accountOrders={accountOrders}
            />
          </Box>
        </Grid>

        <Grid item width="100%" xl={6} data-testid="search-product-list">
          <Block isLoading={accountAutomaticOrdersLoading}>
            <AutoOrderConfigurations
              day={filterParams.day}
              onChange={(value) => setFilterParams({ day: value })}
              accountOrders={accountOrders}
              account={account}
              updateAccountRelated={updateAccountRelated}
            />
          </Block>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManageAutoOrders;

function DisableMultiple({ open, setOpen, onSuccess, updateAccountRelated }) {
  const [date, setDate] = useState(null);
  const [dateTill, setDateTill] = useState(null);
  const [disableTillType, setDisableTillType] = useState("KNOWN_DATE");
  const [selectedCustomersAccounting, setSelectedCustomersAccounting] =
    useState([]);
  function handleSubmit() {
    if (!date) {
      alert("Disable from date is required");
      return;
    }
    if (disableTillType === "KNOWN_DATE" && !dateTill) {
      alert("Disable till date is required");
      return;
    }
    const df = date.format(DATE_FORMAT);
    const dt =
      disableTillType === "UNTIL_FURTHER_NOTICE"
        ? null
        : dateTill
          ? dateTill.format(DATE_FORMAT)
          : null;
    bulkPushOffAutoOrders(
      selectedCustomersAccounting.map((acc) => acc.value),
      df,
      dt,
    ).then(
      (res) => {
        alert(
          disableTillType === "UNTIL_FURTHER_NOTICE"
            ? `successfully disabled from ${df} until further notice`
            : `successfully disabled from ${df} to ${dt}`,
        );
        setOpen(false);
        if (onSuccess) {
          onSuccess();
          updateAccountRelated()
        }
      },
      (err) => {
        alert("Error occurred");
      },
    );
  }
  return (
    <Dialog open={open}>
      <DialogTitle>
        <Typography>Cancel recurring orders</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Stack>
            <Typography>Disable from</Typography>
            <DatePickerCustom2
              controlledValue={date}
              setControlledValue={setDate}
            />
            <Typography mt={1}>Disable till type</Typography>
            <RadioGroup
              row
              value={disableTillType}
              onChange={(e) => {
                const selectedType = e.target.value;
                setDisableTillType(selectedType);
                if (selectedType === "UNTIL_FURTHER_NOTICE") {
                  setDateTill(null);
                }
              }}
            >
              <FormControlLabel
                value="KNOWN_DATE"
                control={<Radio size="small" />}
                label="Known date"
              />
              <FormControlLabel
                value="UNTIL_FURTHER_NOTICE"
                control={<Radio size="small" />}
                label="Until further notice"
              />
            </RadioGroup>
            {disableTillType != "UNTIL_FURTHER_NOTICE" && <><Typography>Disable Till</Typography>
            <DatePickerCustom2
              controlledValue={dateTill}
              setControlledValue={setDateTill}
              additionlStyles={{
                opacity: disableTillType === "UNTIL_FURTHER_NOTICE" ? 0.55 : 1,
                pointerEvents:
                  disableTillType === "UNTIL_FURTHER_NOTICE"
                    ? "none"
                    : "auto",
              }}
            /></>}
            
          </Stack>

          <AccountSelect
            label="Accounts to deactivate for"
            id="multiple-limit-tags-xx"
            value={selectedCustomersAccounting}
            onChange={(_event, newValue) => {
              setSelectedCustomersAccounting(newValue);
            }}
            multiple={true}
            //setCustomerSearch={setCustomerSearchAccounting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"} spacing={1}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            color={"error"}
          >
            Cancel
          </Button>
          <Button variant="contained" color={"success"} onClick={handleSubmit}>
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function ResumeDisabledAccountsModal({
  open,
  setOpen,
  disabledAccounts = [],
  onSuccess,
  updateAccountRelated
}) {
  const [resumeDate, setResumeDate] = useState(null);
  const [selectedAccountsToResume, setSelectedAccountsToResume] = useState([]);
  const disabledAccountOptions = disabledAccounts.map((acc) => ({
    ...acc,
    value: acc.account_id,
    label: acc.account_name,
  }));

  function handleResumeSubmit() {
    if (selectedAccountsToResume.length === 0) {
      alert("Select at least one account to resume");
      return;
    }
    if (!resumeDate) {
      alert("Resume date is required");
      return;
    }
    const accountIds = selectedAccountsToResume.map((acc) => acc.value);
    bulkPushOffAutoOrders(
      accountIds,
      dayjs().format(DATE_FORMAT),
      resumeDate.format(DATE_FORMAT),
    ).then(
      () => {
        alert(
          `successfully scheduled resume for ${selectedAccountsToResume.length} account(s) on ${resumeDate.format(
            DATE_FORMAT,
          )}`,
        );
        setOpen(false);
        setSelectedAccountsToResume([]);
        setResumeDate(null);
        if (onSuccess) {
          onSuccess();
          updateAccountRelated()
        }
      },
      () => {
        alert("Error occurred");
      },
    );
  }

  return (
    <Dialog open={open}>
      <DialogTitle>
        <Typography>Resume disabled accounts</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack>
            <Typography>Accounts to resume</Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={disabledAccountOptions}
              value={selectedAccountsToResume}
              isOptionEqualToValue={(option, value) =>
                String(option.value) === String(value.value)
              }
              classes={{ popper: "autocomplete-popper" }}
              getOptionLabel={(option) => option?.label || ""}
              onChange={(_event, newValue) =>
                setSelectedAccountsToResume(newValue)
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label} <Typography fontSize={12} marginLeft={1}>from {option?.disabled_from && dayjs(option?.disabled_from).format(DATE_FORMAT_FRONT)} Until {option.disabled_till?dayjs(option.disabled_till).format(DATE_FORMAT_FRONT):' further notice'}</Typography>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select disabled accounts" />
              )}
              sx={{
                "& .MuiInputBase-root": {
                  padding: "0 65px 0 9px",
                },
                "& .MuiInputBase-input": {
                  padding: "8.5px 4px 8.5px 5px !important",
                },
                "& .MuiFormControl-root": {
                  minHeight: "40px !important",
                },
              }}
            />
          </Stack>
          <Stack>
            <Typography>Orders resume on</Typography>
            <DatePickerCustom2
              controlledValue={resumeDate}
              setControlledValue={setResumeDate}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"} spacing={1}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            color={"error"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={"success"}
            onClick={handleResumeSubmit}
          >
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
